import { share } from 'rxjs/operators';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  OnInit,
  Output,
  ViewChild,
  Input
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { AuthModalComponent } from '@app/core/user/containers/auth-modal/auth-modal.component';
import { Login, PopinAuthShowed } from '@app/core/user/store/actions';
import * as reducers from '@app/core/user/store/reducers';
import { IAppState } from '@app/store';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { Credentials } from '@vestiaire/api-sdk-angular';

@Component({
  selector: 'vc-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoginComponent implements OnInit {
  @ViewChild('userEmail', { read: ElementRef, static: false })
  userEmail: ElementRef;
  @ViewChild('userPassword', { read: ElementRef, static: false })
  userPassword: ElementRef;
  @Input()
  popinForced: boolean;
  @Output()
  displayResetPassword = new EventEmitter<boolean>();
  @Output()
  currentEmail = new EventEmitter<string>();

  public isChromeApp = false;
  public isMobile = false;

  public displayPassword = false;

  public pending$: Observable<any>;
  public error: any;

  public loginForm: FormGroup;
  public email: FormControl = new FormControl('', [
    Validators.required,
    Validators.pattern(`^[\\w.-]+(\\+[\\w.-]+)?@[\\w.-]+\\.[a-zA-Z]{2,6}$`)
  ]);
  public password: FormControl = new FormControl('', [Validators.required]);

  public formSubmitted;

  constructor(
    private ref: ChangeDetectorRef,
    private store: Store<IAppState>,
    public dialogRef: MatDialogRef<AuthModalComponent>
  ) {
    if (/CriOS/i.test(navigator.userAgent) && /iPhone|iPad|iPod/i.test(navigator.userAgent)) {
      this.isChromeApp = true;
    }
  }

  ngOnInit() {
    this.pending$ = this.store.pipe(select(reducers.isPending), share());

    this.store.pipe(select(reducers.getError)).subscribe(
      res => {
        if (res && res.errors) {
          this.error = res.errors.detail;
        }
      },
      () => {
        // Silent catch error because we don't have anything to do
      }
    );

    this.store.pipe(select(reducers.isAuthenticated)).subscribe(
      res => {
        if (res) {
          this.dialogRef.close('success');
        }
      },
      err => {
        // Silent catch error because we don't have anything to do
      }
    );

    this.createForm();

    this.store.select('device').subscribe(
      res => (this.isMobile = !res.isMdUp),
      err => {}
    );
    this.store.dispatch(
      new PopinAuthShowed({
        type: 'login',
        forced: this.popinForced,
        isMobile: this.isMobile
      })
    );
  }

  createForm() {
    this.loginForm = new FormGroup({
      email: this.email,
      password: this.password
    });
  }

  clearErrorMessage() {
    this.formSubmitted = false;
    this.error = null;
  }

  onSubmit() {
    if (this.loginForm.valid) {
      const credential: Credentials = {
        email: this.email.value,
        password: this.password.value
      };

      this.store.dispatch(new Login(credential));

      this.ref.detectChanges();
    }
  }

  /**
   * We create this function because chrome have a bug with the autofill
   * the autofill is not validating the loginForm
   * so we manually set the data and then trigger the onSubmit
   *
   * @memberof LoginComponent
   */
  onSubmitChrome() {
    if (this.userEmail.nativeElement.value && this.userPassword.nativeElement.value) {
      this.email.setValue(this.userEmail.nativeElement.value);
      this.password.setValue(this.userPassword.nativeElement.value);

      this.onSubmit();
    }
  }

  togglePassword() {
    this.displayPassword = !this.displayPassword;
  }

  displayResetPasswordComponent() {
    this.displayResetPassword.emit(true);
    this.currentEmail.emit(this.userEmail.nativeElement.value);
  }

  stopEnterPropagation($event) {
    if ($event.code === 'Enter') {
      $event.preventDefault();
      $event.stopPropagation();
      this.onSubmit();
    }
  }

  onEmailFocusOut() {
    if (this.email.value.length === 0) {
      this.email.reset();
    }
  }

  onPasswordFocusOut() {
    if (this.password.value.length === 0) {
      this.password.reset();
    }
  }
}
